import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type OverflowTabItem = {
  to: string;
  label: string;
  end?: boolean;
  /** Optional pictogram before the label (e.g. KI). */
  icon?: ReactNode;
};

/** Below this width extra tabs go into „Mehr“. Matches fleet/intranet desktop at `lg`. */
const LG_QUERY = '(min-width: 1024px)';
const GAP_PX = 4;

const tabClassName = ({ isActive }: { isActive: boolean }) =>
  cn(
    '-mb-px inline-flex min-h-11 shrink-0 items-center whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
    isActive
      ? 'border-primary text-primary'
      : 'border-transparent text-muted-foreground hover:text-foreground',
  );

/** Splits tabs into the row and the „Mehr“ menu. The active item always stays in the row. */
export function planOverflowTabs(
  items: OverflowTabItem[],
  {
    containerWidth,
    itemWidths,
    moreWidth,
    activeIndex,
    showAll,
  }: {
    containerWidth: number;
    itemWidths: number[];
    moreWidth: number;
    activeIndex: number;
    showAll: boolean;
  },
): { visible: OverflowTabItem[]; overflow: OverflowTabItem[] } {
  if (items.length === 0) {
    return { visible: [], overflow: [] };
  }
  if (showAll) {
    return { visible: items, overflow: [] };
  }

  const total =
    itemWidths.reduce((sum, width) => sum + width, 0) + GAP_PX * Math.max(0, items.length - 1);
  if (total <= containerWidth) {
    return { visible: items, overflow: [] };
  }

  const available = Math.max(0, containerWidth - moreWidth - GAP_PX);
  const mustKeep = activeIndex >= 0 && activeIndex < items.length ? activeIndex : 0;
  const include = new Set<number>([mustKeep]);
  let used = itemWidths[mustKeep] ?? 0;

  for (let index = 0; index < items.length; index += 1) {
    if (include.has(index)) {
      continue;
    }
    const next = used + GAP_PX + (itemWidths[index] ?? 0);
    if (next <= available) {
      include.add(index);
      used = next;
      continue;
    }
    if (index > mustKeep) {
      break;
    }
  }

  return {
    visible: items.filter((_, index) => include.has(index)),
    overflow: items.filter((_, index) => !include.has(index)),
  };
}

function useIsLgUp(): boolean {
  const [lgUp, setLgUp] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(LG_QUERY).matches;
  });

  useLayoutEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(LG_QUERY);
    const sync = () => setLgUp(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return lgUp;
}

function activeItemIndex(items: OverflowTabItem[], pathname: string): number {
  return items.findIndex((item) =>
    Boolean(matchPath({ path: item.to, end: item.end ?? false }, pathname)),
  );
}

function TabLabel({ item }: { item: OverflowTabItem }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {item.icon}
      <span>{item.label}</span>
    </span>
  );
}

/**
 * Product tab strip: below `lg` overflow goes into „Mehr“ so labels stay complete;
 * from `lg` all tabs stay in one row (`overflow-x-auto` if needed). The active
 * item always stays in the row when it fits with „Mehr“.
 */
export function OverflowTabNav({
  ariaLabel,
  items,
  className,
}: {
  ariaLabel: string;
  items: OverflowTabItem[];
  className?: string;
}) {
  const location = useLocation();
  const lgUp = useIsLgUp();
  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [plan, setPlan] = useState(() => ({
    visible: items.slice(0, 1),
    overflow: items.slice(1),
  }));

  const itemsKey = items
    .map((item) => `${item.to}\0${item.label}\0${item.end ? '1' : '0'}`)
    .join('\n');

  useLayoutEffect(() => {
    if (lgUp) {
      return;
    }

    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure) {
      return;
    }

    const recompute = () => {
      const tabNodes = Array.from(measure.querySelectorAll<HTMLElement>('[data-measure="tab"]'));
      const moreNode = measure.querySelector<HTMLElement>('[data-measure="more"]');
      const itemWidths = tabNodes.map((node) => node.offsetWidth);
      const moreWidth = moreNode?.offsetWidth ?? 0;
      setPlan(
        planOverflowTabs(items, {
          containerWidth: nav.clientWidth,
          itemWidths,
          moreWidth,
          activeIndex: activeItemIndex(items, location.pathname),
          showAll: false,
        }),
      );
    };

    recompute();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(recompute);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [items, itemsKey, location.pathname, lgUp]);

  const visible = lgUp ? items : plan.visible;
  const overflow = lgUp ? [] : plan.overflow;

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      className={cn(
        'relative flex min-h-11 w-full min-w-0 gap-1 border-b',
        lgUp
          ? 'flex-nowrap overflow-x-auto overflow-y-hidden'
          : 'flex-nowrap overflow-x-clip overflow-y-hidden',
        className,
      )}
    >
      {!lgUp ? (
        <div
          ref={measureRef}
          className="pointer-events-none invisible absolute top-0 left-0 h-0 overflow-hidden"
          aria-hidden
        >
          <div className="inline-flex gap-1">
            {items.map((item) => (
              <span key={item.to} data-measure="tab" className={tabClassName({ isActive: false })}>
                <TabLabel item={item} />
              </span>
            ))}
            <span data-measure="more" className={tabClassName({ isActive: false })}>
              Mehr
            </span>
          </div>
        </div>
      ) : null}

      {visible.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end ?? false} className={tabClassName}>
          <TabLabel item={item} />
        </NavLink>
      ))}

      {overflow.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(tabClassName({ isActive: false }), 'shrink-0')}
            aria-label="Mehr"
          >
            Mehr
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflow.map((item) => (
              <DropdownMenuItem key={item.to} asChild>
                <NavLink to={item.to} end={item.end ?? false}>
                  <TabLabel item={item} />
                </NavLink>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </nav>
  );
}
